import toast from '@components/Common/Toast.js';
import Tooltip from '@components/Common/Tooltip.js';
import { IS_APP, IS_MOBILE } from '@constants/global';
import {
  ChevronIcon,
  ExpandPreviewIcon,
  PaperClipIcon,
  PlusIcon,
  TypeIcon,
  XIcon,
} from '@icons/IconLibrary';
import { route } from '@router/index';
import { UIButton, UIDiv, UIImage, UIInput, UIText } from '@ui';
import { updateColumnValue } from '@utils/columnUtils.js';
import i18next from 'i18next';
import AttachedFilesModal from './AttachedFilesModal.js';

export default class TextCell extends UIDiv {
  constructor({
    column,
    item = {},
    value = '',
    canEdit = true,
    isTask,
    onValueChange = null,
    addDragHandle = null,
    setCellEditingState = null,
  }) {
    super();

    this.column = column;
    this.item = item;
    this.value = value;
    this.isEditMode = false;
    this.canEdit = canEdit;
    this.isTask = isTask;
    this.onValueChange = onValueChange; // Callback for value changes instead of API calls
    this.isTaskCreationMode = !!this.onValueChange;
    this.addDragHandle = addDragHandle; // Function to add drag handle
    this.setCellEditingState = setCellEditingState;

    // Initialize modal for tasks with attached files
    this.attachedFilesModal = null;
    if (
      this.isTask &&
      this.item.reconstructions &&
      this.item.reconstructions.length > 0
    ) {
      this.attachedFilesModal = new AttachedFilesModal({
        task: this.item,
        onFileSelect: reconstruction => {
          this.#handleFileSelect(reconstruction);
        },
      });
    }

    this.addClass('flex items-center min-h-[34px]');

    this.#render();
  }

  #render() {
    this.clear();

    // if drag handle is provided, add it
    if (this.canEdit && this.addDragHandle) {
      try {
        this.addDragHandle(this);
      } catch (error) {
        console.warn(
          'TextCell: Failed to add drag handle - parent row may have been destroyed',
          error,
        );
      }
    }

    // If the cell is in edit mode, render the input
    if (!this.canEdit || (this.item.isDefault && !this.column.isCustom)) {
      this.#renderAnchorOrThumbnail();
      this.#renderUneditableInput();
      return;
    }

    if (this.value.trim() === '') {
      this.#renderEmptyState();
    } else {
      this.#renderAnchorOrThumbnail();
      this.#renderTextState();
    }
  }
  #renderAnchorOrThumbnail() {
    if (this.isTaskCreationMode) return; // Skip if onValueChange is provided as we don't need to render anchor or thumbnail
    if (this.column.key === 'name' && !this.column.isCustom) {
      if (this.isTask) {
        this.#renderOpenTaskButton();
      } else {
        this.#renderOpenProjectButton();
      }
    }
  }

  #renderEmptyState() {
    const emptyContainer = new UIDiv();
    emptyContainer.addClass(
      'w-full group hover:border hover:border-solid hover:border-gray-300 hover:bg-gray-50 rounded-md min-h-[34px] cursor-text border border-dashed border-border"',
    );

    // Hover state: show TypeIcon and plus
    const hoverContent = new UIDiv();
    hoverContent.addClass(
      'p-1 px-2 hidden group-hover:!flex items-center gap-1.5',
    );

    const calendarIcon = new TypeIcon({
      size: 18,
      color: 'none',
    });
    const plusIcon = new PlusIcon({
      size: 12,
      className: 'bg-[var(--o-hive-primary-80)] rounded-full',
      strokeColor: 'white',
    });
    hoverContent.add(calendarIcon, plusIcon);
    emptyContainer.add(hoverContent);

    emptyContainer.setAttribute('tabindex', '0');
    emptyContainer.onKeyDown(event => {
      const isEditable = !this.item.isDefault;
      if (event.key === 'Enter' && !this.isEditMode && isEditable) {
        event.preventDefault();
        this.enterEditMode();
      }
    });

    // Click handler to enter edit mode
    emptyContainer.onClick(() => {
      if (IS_MOBILE() && this.column.key === 'name' && !this.column.isCustom) {
        this.#navigateToItem();
      } else {
        this.enterEditMode();
      }
    });

    this.add(emptyContainer);
  }

  #renderTextState() {
    const textContainer = new UIDiv();
    textContainer.addClass(
      'truncate w-full group relative rounded-md min-h-[34px] cursor-pointer flex items-center gap-0.5',
    );

    const textContent = new UIText(this.value);
    textContent.addClass(
      'truncate cursor-text group-hover:bg-white group-hover:px-2 group-hover:py-1 group-hover:rounded group-hover:border group-hover:border-gray-300 transition-all duration-200',
    );
    textContainer.add(textContent);

    // Click handler to enter edit mode - only on the text content
    textContent.onClick(event => {
      event.stopPropagation(); // Prevent event from bubbling up to textContainer
      if (
        IS_MOBILE() &&
        this.column.key === 'name' &&
        !this.column.isCustom &&
        !this.isTaskCreationMode
      ) {
        this.#navigateToItem();
      } else {
        // On desktop or non-name columns, click enters edit mode
        this.enterEditMode();
      }
    });
    textContent.setAttribute('tabindex', '0');
    textContent.onKeyDown(event => {
      const isEditable = !this.item.isDefault;
      if (event.key === 'Enter' && !this.isEditMode && isEditable) {
        event.preventDefault();
        this.enterEditMode();
      }
    });

    // Add mobile touch handling for name columns
    if (IS_MOBILE() && this.column.key === 'name' && !this.column.isCustom) {
      let longPressTimer = null;
      const longPressDelay = 500; // 500ms for long press

      // Long press on textContent enters edit mode
      textContent.onTouchStart(event => {
        event.stopPropagation(); // Prevent touch events from bubbling
        longPressTimer = setTimeout(() => {
          this.enterEditMode();
        }, longPressDelay);
      });

      textContent.onTouchEnd(event => {
        event.stopPropagation(); // Prevent touch events from bubbling
        if (longPressTimer) {
          clearTimeout(longPressTimer);
          longPressTimer = null;
        }
      });

      textContent.onTouchMove(event => {
        event.stopPropagation(); // Prevent touch events from bubbling
        if (longPressTimer) {
          clearTimeout(longPressTimer);
          longPressTimer = null;
        }
      });

      // Long press on the entire textContainer also enters edit mode
      textContainer.onTouchStart(() => {
        longPressTimer = setTimeout(() => {
          this.enterEditMode();
        }, longPressDelay);
      });

      textContainer.onTouchEnd(() => {
        if (longPressTimer) {
          clearTimeout(longPressTimer);
          longPressTimer = null;
        }
      });

      textContainer.onTouchMove(() => {
        if (longPressTimer) {
          clearTimeout(longPressTimer);
          longPressTimer = null;
        }
      });
    }

    // Add expandable icon on the right side for navigation
    if (
      !IS_MOBILE() &&
      this.column.key === 'name' &&
      !this.column.isCustom &&
      !this.isTaskCreationMode &&
      // avoid showing on task as we have thumbnail already + files badge now so it makes the UI too crowded
      !this.isTask
    ) {
      const expandIconContainer = new UIDiv();
      expandIconContainer.addClass(
        'ml-auto flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-1 hover:bg-gray-200 rounded expand-icon-container pointer-events-none group-hover:pointer-events-auto',
      );

      const expandIcon = new ExpandPreviewIcon({
        size: 16,
        color: 'none',
        className: 'text-gray-500 hover:text-gray-700',
      });

      expandIconContainer.add(expandIcon);

      // Add tooltip functionality
      expandIconContainer.onMouseEnter(() => {
        Tooltip.show(
          expandIconContainer,
          this.isTask ? 'Open Task' : 'Open task list',
          'top',
        );
      });
      expandIconContainer.onMouseLeave(() => {
        Tooltip.hide();
      });

      expandIconContainer.onClick(event => {
        event.stopPropagation(); // Prevent event from bubbling up
        this.#navigateToItem();
      });

      textContainer.add(expandIconContainer);
    }

    if (this.column.key !== 'name' && this.column.isCustom) {
      // hover show clear icon on right
      const clearIcon = new XIcon({
        size: 12,
        className:
          'absolute right-2 top-1/2 transform -translate-y-1/2 hidden group-hover:!flex justify-center items-center bg-gray-200 hover:bg-gray-300 rounded-md cursor-pointer !w-5 !h-5',
      });
      clearIcon.onClick(async event => {
        event.stopPropagation(); // Prevent triggering the click handler
        try {
          // If onValueChange callback is provided, use it instead of API call
          if (this.onValueChange) {
            this.onValueChange('');
            // Update the local value
            this.value = '';
            if (this.item) {
              this.item[this.column.key] = '';
            }
            this.exitEditMode();
          } else {
            // Update the value via API if we have the necessary information
            const entityType = !this.item.reconstructions ? 'folder' : 'file';
            const entityId = this.item.id || this.item.sharingId;

            await updateColumnValue(this.column.id, entityType, entityId, '');

            // Update the local value
            this.value = '';
            if (this.item) {
              this.item[this.column.key] = '';
            }
            this.exitEditMode();
          }
        } catch (error) {
          toast.error(i18next.t('pages.projects.toast.failed_column_update'));
          console.error('Failed to update column value:', error);
        }
      });
      textContainer.add(clearIcon);
    }

    // Add attachment badge for tasks with files
    if (
      this.isTask &&
      this.item.reconstructions &&
      this.item.reconstructions.length > 0 &&
      this.column.key === 'name' &&
      !this.column.isCustom
    ) {
      const attachmentBadge = this.#createAttachmentBadge();
      textContainer.add(attachmentBadge);
    }

    this.add(textContainer);
  }

  #createAttachmentBadge() {
    const badge = new UIButton();
    badge.addClass(
      'ml-auto flex items-center gap-1.5 px-2.5 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-full text-xs font-medium transition-colors duration-200 flex-shrink-0',
    );

    const paperclipIcon = new PaperClipIcon({
      size: 12,
      color: 'none',
    });

    const count = new UIText(this.item.reconstructions.length.toString());
    count.addClass('leading-none');

    badge.add(paperclipIcon, count);

    // Add tooltip
    badge.onMouseEnter(() => {
      Tooltip.show(
        badge,
        `${this.item.reconstructions.length} attached file${this.item.reconstructions.length !== 1 ? 's' : ''}`,
        'top',
      );
    });
    badge.onMouseLeave(() => {
      Tooltip.hide();
    });

    // Click handler
    badge.onClick(event => {
      event.stopPropagation();
      this.#showAttachedFilesModal();
    });

    badge.onKeyDown(event => {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        this.#showAttachedFilesModal();
      }
    });

    return badge;
  }

  #showAttachedFilesModal() {
    if (this.attachedFilesModal) {
      this.attachedFilesModal.show();
    }
  }

  enterEditMode() {
    if (this.isEditMode) return;

    this.isEditMode = true;

    // Notify parent row that this cell is entering edit mode
    if (typeof this.setCellEditingState === 'function') {
      this.setCellEditingState(true);
    }

    this.clear();

    // Create input that fills the cell
    const input = new UIInput(this.value).setType('text');
    input.addClass(
      'w-full min-h-[34px] cursor-text rounded-md px-2 focus:outline-none focus:!ring-2 focus:!ring-[var(--o-hive-primary-80)]',
    );

    this.add(input);

    // Focus the input and select all text
    setTimeout(() => {
      input.dom.focus();
      if (this.value) {
        input.dom.select();
      }
    }, 0);

    let isEditingSaved = false;

    // Handle clicking outside the cell to exit edit mode
    const handleClickOutside = event => {
      if (isEditingSaved) return;

      // Check if the click is outside this cell
      if (!this.contains(event.target)) {
        document.removeEventListener('click', handleClickOutside);
        saveEdit(event);
      }
    };

    // Add the click outside listener with a slight delay to avoid immediate triggering
    setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);

    // Handle save on Enter or blur
    const saveEdit = async event => {
      if (isEditingSaved) return;
      isEditingSaved = true;

      // Clean up the click outside listener
      document.removeEventListener('click', handleClickOutside);

      const newValue = input.getValue().trim();
      if (newValue === this.value) {
        this.exitEditMode();
        return;
      }

      // if the cell is name and not isCustom then it cannot be empty
      if (
        this.column.key === 'name' &&
        !this.column.isCustom &&
        newValue === ''
      ) {
        toast.error(i18next.t('pages.projects.nameCannotBeEmpty'));
        this.exitEditMode();
        return;
      }

      try {
        // If onValueChange callback is provided, use it instead of API call
        if (this.onValueChange) {
          this.onValueChange(newValue, event);
          // Update the local value
          this.value = newValue;
          if (this.item) {
            this.item[this.column.key] = newValue;
          }
          this.exitEditMode();
        } else {
          // Update the value via API if we have the necessary information
          const entityType = !this.item.reconstructions ? 'folder' : 'file';
          const entityId = this.item.id || this.item.sharingId;

          await updateColumnValue(
            this.column.id,
            entityType,
            entityId,
            newValue,
            // we want to rename the folder/task name too
            this.column.key === 'name' && !this.column.isCustom,
          );

          // Update the local value
          this.value = newValue;
          if (this.item) {
            this.item[this.column.key] = newValue;
          }

          this.exitEditMode();
        }
      } catch (error) {
        toast.error(i18next.t('pages.projects.toast.failed_column_update'));
        console.error('Failed to update column value:', error);
        // Revert to original value on error
        this.exitEditMode();
      }
    };

    // Handle cancel on Escape
    const cancelEdit = () => {
      if (isEditingSaved) return;
      isEditingSaved = true;
      document.removeEventListener('click', handleClickOutside);
      this.exitEditMode();
    };

    // Add event listeners
    input.onKeyDown(event => {
      if (event.key === 'Enter') {
        event.preventDefault();
        saveEdit(event);
      } else if (event.key === 'Escape') {
        cancelEdit();
      }
    });

    input.onBlur(saveEdit);
  }

  exitEditMode() {
    this.isEditMode = false;

    // Notify parent row that this cell is exiting edit mode
    if (typeof this.setCellEditingState === 'function') {
      this.setCellEditingState(false);
    }

    this.#render();
  }

  #renderUneditableInput() {
    const textContainer = new UIDiv();
    textContainer.addClass(
      'truncate w-full group relative rounded-md min-h-[34px] cursor-pointer flex items-center gap-0.5',
    );

    const textContent = new UIText(this.value);
    textContent.addClass('truncate');
    textContainer.add(textContent);

    if (
      !IS_MOBILE() &&
      this.column.key === 'name' &&
      !this.column.isCustom &&
      !this.isTaskCreationMode
    ) {
      const expandIconContainer = new UIDiv();
      expandIconContainer.addClass(
        'ml-auto flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-1 hover:bg-gray-200 rounded expand-icon-container pointer-events-none group-hover:pointer-events-auto',
      );

      const expandIcon = new ExpandPreviewIcon({
        size: 16,
        color: 'none',
        className: 'text-gray-500 hover:text-gray-700',
      });

      expandIconContainer.add(expandIcon);

      // Add tooltip functionality
      expandIconContainer.onMouseEnter(() => {
        Tooltip.show(
          expandIconContainer,
          this.isTask ? 'Open Task' : 'Open task list',
          'top',
        );
      });
      expandIconContainer.onMouseLeave(() => {
        Tooltip.hide();
      });

      expandIconContainer.onClick(event => {
        event.stopPropagation(); // Prevent event from bubbling up
        this.#navigateToItem();
      });

      textContainer.add(expandIconContainer);
    }

    textContent.onClick(() => {
      if (
        IS_MOBILE() &&
        this.column.key === 'name' &&
        !this.column.isCustom &&
        !this.isTaskCreationMode
      ) {
        this.#navigateToItem();
      }
    });

    this.add(textContainer);
  }

  #renderOpenProjectButton() {
    const openContainer = new UIButton();
    openContainer.addClass('mr-2 ml-0.5');

    const openIcon = new ChevronIcon({
      size: 16,
      color: 'none',
    });
    openContainer.add(openIcon);

    openContainer.onKeyDown(event => {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        this.#triggerNavigationEvent();
      }
    });

    openContainer.onClick(event => {
      event.stopPropagation();
      this.#triggerNavigationEvent();
    });

    this.add(openContainer);
  }

  #triggerNavigationEvent() {
    const event = new CustomEvent('navigateToFolder', {
      detail: {
        folderId: this.item.id,
        folderName: this.value,
        item: this.item,
      },
      bubbles: true,
    });
    this.dom.dispatchEvent(event);
  }

  #renderOpenTaskButton() {
    const thumbnailContainer = new UIDiv();
    thumbnailContainer.setClass(
      'thumbnail-container mx-2 min-w-[43px] min-h-[34px] mr-2 flex items-center justify-center shadow-md bg-white rounded-md',
    );
    const thumbnail = new UIImage(
      this.item.thumbnail || '/static/images/logos/bee_blue.svg',
      'white',
      15,
    );
    thumbnail.setClass(
      'h-8 object-cover rounded-sm cursor-pointer transition-opacity duration-200 hover:opacity-80',
    );
    thumbnailContainer.add(thumbnail);

    thumbnailContainer.setAttribute('tabindex', '0');
    thumbnailContainer.onKeyDown(event => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.#openTaskViewer();
      }
    });
    thumbnailContainer.onClick(() => {
      this.#openTaskViewer();
    });

    this.add(thumbnailContainer);
  }

  #openTaskViewer() {
    Tooltip.hide();
    const params = new URLSearchParams(window.location.search);
    params.set('sharingId', this.item.sharingId);
    params.set('from', 'projects');
    params.set('pId', this.item.parentId);
    if (IS_APP()) {
      params.set('isApp', 'true');
    }
    const newSearch = params.toString();
    const newUrl = `/viewer?${newSearch}`;
    route(newUrl);
  }

  #navigateToItem() {
    Tooltip.hide();
    if (this.isTask) {
      this.#openTaskViewer();
    } else {
      this.#triggerNavigationEvent();
    }
  }

  #handleFileSelect(reconstruction) {
    // Open the task viewer with the specific file
    const params = new URLSearchParams(window.location.search);
    params.set('sharingId', this.item.sharingId);
    params.set('from', 'projects');
    params.set('pId', this.item.parentId);
    if (IS_APP()) {
      params.set('isApp', 'true');
    }
    // Add file-specific parameter if needed
    if (reconstruction.reconstructionID) {
      params.set('reconstructionId', reconstruction.reconstructionID);
    }
    const newSearch = params.toString();
    const newUrl = `/viewer?${newSearch}`;
    route(newUrl);
  }
}
